import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import equal from 'fast-deep-equal/es6';
import { appStatuses, errors, routes } from '../../constants';
import { sections } from '../../constants/sections';
import { getApplication, getCurrentProfile, saveCuf, submitCuf } from '../../api/cuf';
import { getBadgeInfo } from '../../api/company';
import { NotificationsContext } from '../../context/Notifications.context';
import { AuthContext } from '../../context/Auth.context';
import { ThemeContext } from '../../context/Theme.context';
import { ProfileContext } from '../../context/Profile.context';
import { withRouter } from '../../components/WithRouter';
import ProfileNav from '../../components/ProfileNav';
import Loading from '../../components/Loading';
import GlobalActions from '../../components/GlobalActions';
import { Box, Typography, useResponsive } from '@otcmarketsgroup/otcm-ui-lib';
import FormSupportActions from '../../components/FormSupportActions';
import SubmitInProgressDialog from './SubmitInProgressDialog';

const FormPage = ({ params: { formSection, symbol }, navigate }) => {
  const [authState] = useContext(AuthContext);
  const [notificationState, dispatchNotification] = useContext(NotificationsContext);
  const theme = useContext(ThemeContext);
  const [profileState, dispatchProfile] = useContext(ProfileContext);
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const profile = profileState.profile;
  const backupProfile = profileState.backupProfile;
  const user = authState?.user;
  const userEmail = user?.email;
  const { isDesktop } = useResponsive();

  useEffect(() => {
    if (profileState.isSubmitted) {
      navigate(`${routes.PROFILE_PREFIX}/${symbol}/success`);
    }
  }, [profileState.isSubmitted, navigate, symbol]);

  useEffect(() => {
    if (!userEmail) return;

    const params = {
      symbol,
      email: userEmail
    };

    getBadgeInfo(symbol)
      .then(badgeInfo => {
        dispatchProfile({
          type: 'SET_VERIFIED_PROFILE',
          verifiedProfile: badgeInfo ? badgeInfo.verifiedProfile : false,
          verifiedDate: badgeInfo ? badgeInfo.verifiedDate : null
        });
      })
      .catch(err => {
        console.error('Could not retrieve company verify status');

        dispatchProfile({
          type: 'SET_PROFILE',
        payload: err
      });
    });

    getApplication(params)
      .then(data => {
        dispatchProfile({
          type: 'SET_PROFILE',
          payload: { ...data }
        });
      })
      .catch(err => {
        if (err.status === 412) {
          fetchCurrentProfile();
        } else {
          setError(errors.FAILED_PROFILE);
        }
      });
  }, [symbol, dispatchProfile, userEmail]);

  const fetchCurrentProfile = () => {
    const params = {
      symbol,
      email: userEmail
    };

    getCurrentProfile(params)
      .then(data => {
        dispatchProfile({
          type: 'SET_PROFILE',
          payload: { ...data, status: { id: appStatuses.NO_APPLICATION } }
        });
      })
      .catch(err => {
        console.error('FAILED TO GET CURRENT PROFILE: ', err);
        setError(errors.FAILED_PROFILE);
      });
  };

  const checkAppChanges = _ => {
    if (profile && !equal(backupProfile, profile)) {
      saveApplication();
    }
  };

  const saveApplication = async callback => {
    const data = {
      cufId: profile.openCufId,
      email: userEmail,
      formData: profile
    };

    try {
      const response = await saveCuf(data);

      dispatchProfile({
        type: 'SET_PROFILE',
        payload: response.formData
      });

      displaySaveNotification();

      callback && callback(true, null);
    } catch (err) {
      callback && callback(false, null);

      displayFailNotification();
    }
  };

  useEffect(() => {
    checkAppChanges();

    window.scroll({
      top: 0,
      behavior: 'smooth'
    });
  }, [formSection]);

  const displaySaveNotification = _ => {
    const id = new Date().getTime();

    dispatchNotification({
      type: 'SAVE_APPLICATION_SUCCESS'
    });
  };

  const displayFailNotification = _ => {
    const id = new Date().getTime();

    dispatchNotification({
      type: 'FAILED_SAVE_APPLICATION',
    });
  };

  const handleSubmit = (submitType, appDiff) => {
    if (profile && !equal(backupProfile, profile)) {
      saveApplication((data, error) => {
        if (data) {
          console.log('API call successful:', data);
          // Handle the data as needed
          postSubmit(submitType, appDiff);
        } else if (error) {
          console.error('API call error:', error);
          // Handle the error as needed
        }
      });
    } else postSubmit(submitType, appDiff);
  };

  const postSubmit = (submitType, appDiff) => {
    const data = {
      cufId: profile.openCufId,
      submittedBy: {
        email: userEmail,
        firstName: user?.firstName,
        lastName: user?.lastName
      },
      appDiff
    };

    setSubmitting(true);

    submitCuf(data).then(_ => {
      setSubmitting(false);
      dispatchProfile({
        type: 'SET_SUCCESS',
        submitType
      });
    })
      .catch(err => {
        setSubmitting(false);
        setError(errors.FAILED_SUBMIT);
        dispatchNotification({
          type: 'FAILED_SUBMIT_APPLICATION',
        });

        console.error(err);
      });
  };

  const getAppSection = () => {
    // if (!formSection) navigate(`${routes.PROFILE_PREFIX}/${symbol}/${routes.COMPANY_INFO}`);
    const section = sections && sections.find(section => section.path === formSection);
    const Component = section ? section.component : sections[0].component;

    return <Component saveApplication={saveApplication} handleSubmit={handleSubmit} isSubmitting={isSubmitting} />;
  };

  if (error) {
    return <Typography variant='subtitle1' color="warning">{error}</Typography>;
  }

  return (
    <Loading loaded={profileState.isLoaded}>
      <SubmitInProgressDialog open={isSubmitting} isError={!!error} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { mobile: "1fr", desktop: "275px 1fr 300px" },
          gap: 2,
        }}
      >
        {/* Left Column - Task Navigation */}
        <Box sx={{ position: { desktop: "sticky" }, top: { desktop: 0 }, alignSelf: { desktop: "start" }, order: { desktop: 1 } }}>
          <ProfileNav />
        </Box>

        {/* Main Content */}
        <Box sx={{ order: { desktop: 2 } }}>
          <main>
            {getAppSection()}
          </main>
        </Box>

        {/* Right Column - Status Panel */}
        <Box sx={{ position: { desktop: "sticky" }, top: { desktop: 0 }, alignSelf: { desktop: "start" }, order: { mobile: -1, desktop: 3 } }}>
          <GlobalActions />
        </Box>
        {!isDesktop && <FormSupportActions />}
      </Box>
    </Loading>
  );
};

FormPage.propTypes = {
  params: PropTypes.shape({
    symbol: PropTypes.string,
    formSection: PropTypes.string
  })
};

export default withRouter(FormPage);
